
import { Call } from "./Call";
import { Conference } from "./Conference";
import { Device } from "./Device";
import { Phone } from "./Phone";
import { EventEmitter } from "./EventEmitter";
import * as anCti from "./ancti";
import { PromiseWithResolvers } from "./PromiseWithResolvers";
import { WebHID } from "./WebHID";

export type ScaleResolutionDown = {
	video1: number,
	video2: number,
	video3: number,
	display: number,
}

export class Agent implements anCti.Agent {

	private readonly DEFAULT = {
		keepaliveTimeout: 60, // seconds
		keepaliveInterval: 120, // seconds
		keepaliveIntervalCall: 10, // seconds
		invocationTimeout: 0, // seconds
		rtcStatsInterval: 5, // seconds
	}

	static window: any = typeof window == 'undefined' ? {} : window;
	static scriptSrc = Agent.window?.document?.currentScript?.getAttribute("src");

	protected invokeId: number = 0;
	protected invokeIdPrefix = ''; // allows to identify invokeId-originators for debugging
	protected ws: any;
	protected reconnecting?: boolean;
	protected url?: string | null;
	protected username?: string;
	protected password?: string;
	protected accessKey?: string;
	protected sessionDuration?: number;
	protected sessionID?: string;
	protected sessionDevice?: string;
	protected applicationID: string = "ancti";
	protected authentication: string = "digest";
	protected requestedSessionDuration?: number;
	protected clickToCall?: anCti.ClickToCallOptions;
	protected guest?: anCti.ApplicationSessionGuestOptions;
	protected startApplicationSessionPromise?: PromiseWithResolvers<anCti.StartApplicationSessionResponse>;
	protected profile?: string;
	protected processRtcEvents: boolean = true;

	protected invocations: Record<string,any> = {};
	protected queue: any[] = [];
	
	protected reconnectDelayMin = 1000;
	protected reconnectDelayMax = 5000;
	protected reconnectDelay = 0;
	protected reconnectTimeout: any;
	
	protected keepaliveTimeout = 0; // 0 means use default
	protected keepaliveInterval = 0;// 0 means use default
	protected keepaliveIntervalCall = 0;// 0 means use default
	protected keepaliveResponseTimeout: any;
	protected keepaliveSendTimeout: any;

	protected rtcStatsInterval = 0;
	protected rtcStatsTimer: any;
	protected invocationTimeout = 0;
	protected invocationTimer:  any;
	
	protected devices: Device[] = [];
	protected monitors: Record<string,Device> = {};
	protected eventEmitter = new EventEmitter();
	protected phone?: Phone;

	connected?: boolean;
	config?: any = {};
	token?: string | boolean;
	audioDeviceId?: string;
	videoDeviceId?: string;
	separateStreams?: boolean;

	remoteAudioTrackProperties?: MediaStreamTrack;
	remoteVideoTrackProperties?: MediaStreamTrack;
   
    videoConstraints?: MediaTrackConstraints;
    audioConstraints?: MediaTrackCapabilities;
	displayConstraints?: anCti.GetDisplayMediaConstraints;

    audioReceiverProperties?: RTCRtpReceiver;
    videoReceiverProperties?: RTCRtpReceiver;
  	rtcConfiguration: any;

	mapRemoteSdp?: (sdp?:string) => string;
	mapLocalSdp?: (sdp?:string) => string;
	
	externalAudio?: (request:anCti.ExternalAudioRequest) => Promise<RTCSessionDescription>|null;

	userAgent?: string;

	hid?: WebHID;
	
	error = console.error.bind(console);
	warn = console.warn.bind(console);
	info = console.info.bind(console);
	debug = console.debug.bind(console);
	table = console.table.bind(console);

	// For backward-compatiblity we allow an applicatio to use its own conference-logic.
	customConferences = false;
	
	scaleResolutionDown: ScaleResolutionDown = {
		video1: 4,
		video2: 2,
		video3: 1,
		display: 1,
	}

	constructor() {
		// per default take the same url as the library was loaded from
		this.url = Agent.scriptSrc;
		if (!this.url?.startsWith("http") && Agent.window?.location) {
			if (this.url?.startsWith("/")) {
				this.url = Agent.window.location.origin + this.url;
			} else {
				this.url = Agent.window.location.href;
			}
		}
		if (this.url) {
			this.url = this.url.replace(/^http/, "ws")
			this.url = this.url.replace(/\/cti\/.*$/, "/cti/ws");
			this.debug("url", this.url);
		}

		const hid = (Agent.window as any)?.navigator?.hid;
		if (hid) {
			this.hid = new WebHID(this,hid);
		}
	}

	stringify() {
		return "[Agent]";
	}

	notify(eventName: string, event: any = {}) {
		this.info("notify", eventName, event);
		this.eventEmitter.notify(eventName, event);
	}

	getMediaDeviceId(media: string): string|undefined {
		if (media == "audio") return this.audioDeviceId;
		if (media == "video") return this.videoDeviceId;
		return undefined;
	}

	getUserMedia(call?: Call, constraints?: MediaStreamConstraints): Promise<MediaStream|undefined> {
		return navigator?.mediaDevices.getUserMedia(constraints);
	}

	getDisplayMedia(call?: Call, constraints?: anCti.GetDisplayMediaConstraints): Promise<MediaStream|undefined> {
		return navigator?.mediaDevices.getDisplayMedia(constraints);
	}

	public on(eventName: string, fn: (event: any) => void) {
		this.eventEmitter.on(eventName, fn);
	}

	public off(eventName: string, fn: (event: any) => void) {
		this.eventEmitter.off(eventName, fn);
	}

	urlParam(name: string): string | null {
		return new URL(Agent.window?.location.href).searchParams.get(name);
	}

	getDevice(deviceID: string): Device {
		// map "pbx."" domains to "ou." 
		deviceID = deviceID?.replace(/(.*)@pbx\.(\d+)$/, "$1@ou.$2");
		let device = this.devices.find(d => d.deviceID == deviceID);
		if (!device) {
			device = new Device(this, deviceID)
			this.devices.push(device)
		}
		return device;
	}

	getDevices(): Device[] {
		return this.devices;
	}

	getPhone(): Phone {
		if (!this.phone) {
			this.phone = new Phone(this);
		}
		return this.phone;
	}

	waitWarmupICEComplete(pc:RTCPeerConnection, timeoutMs = 10000) {
		return new Promise((resolve) => {
			if (pc.iceGatheringState === "complete") {
				return resolve("complete");
			}

			let done = false;

			const finish = (result: string) => {
				if (!done) {
					done = true;
					pc.removeEventListener("icegatheringstatechange", onGatherChange);
					pc.removeEventListener("icecandidate", onCandidate);
					resolve(result);
				}
			};

			const onGatherChange = () => {
				if (pc.iceGatheringState === "complete") {
					finish("complete");
				}
			};

			const onCandidate = (e: RTCPeerConnectionIceEvent) => {
				if (e.candidate && e.candidate.type === "srflx") {
					finish("warm enough");
				}
			};

			pc.addEventListener("icegatheringstatechange", onGatherChange);
			pc.addEventListener("icecandidate", onCandidate);

			setTimeout(() => finish("timeout"), timeoutMs);
		});
	}

	async warmupICEConnection() {
		try {
			this.info("ICE warm-up starts...");

			const pc = new RTCPeerConnection();

			pc.createDataChannel("warmup");
			await pc.setLocalDescription(await pc.createOffer({ offerToReceiveAudio: true }));
			const state = await this.waitWarmupICEComplete(pc, 10000);

			try { pc.close(); } catch {}
			this.info("ICE warm-up complete");
		}
		catch (err) {
			this.error("ICE warm-up failed", err);
		}
	}

	getSessionDevice(): Device | undefined {
		if (this.sessionDevice) {
			return this.getDevice(this.sessionDevice);
		}
		return undefined;
	}

	registerMonitor(deviceID: string, monitorCrossRefID: string): Device {
		let device = this.getDevice(deviceID);
		device.monitorCrossRefID = monitorCrossRefID;
		this.monitors[monitorCrossRefID] = device;
		return device;
	}

	setAudioDeviceId(deviceId: string) {
		this.audioDeviceId = deviceId;
		this.debug("audioDeviceId is", deviceId);
	}

	setVideoDeviceId(deviceId: string) {
		this.videoDeviceId = deviceId;
		this.debug("videoDeviceId is", deviceId);
	}

	send(message: any) {
		this.removeUndefinedMembers(message);
		if (!this.connected) {
			// I think this is a bug in the application: It sends requests
			// before it got the configuration from the server.
				// For backward-compatility I just raise a warning
			this.warn("sending without connection",message)
			this.queue.push(message);
		} else {
			this.sendToSocket(message);
		}
	}

	private sendToSocket(message:any) {
		let text = JSON.stringify(message);
		this.info("JSON send:", text);
		try {
			this.ws?.send(text);
		} catch (error) {
			this.error("websocket cannot send", error);
			this.ws = undefined;
		}
	}

    private checkStartInvocationTimer(restart: boolean) {
		if (this.invocationTimer) {
			if (!restart) {
				// timer is already running
				return;
			}
			clearTimeout(this.invocationTimer);
		}
		// Just start timeout if value is configured
		const timeout = this.getConfigValue("invocationTimer");
		if (timeout> 0) {
			this.invocationTimer = setTimeout(() => {
				this.handleInvocationTimeout(); 
			}, timeout* 1000);
		}
	}

    private handleInvocationTimeout() {
        // Handle timeout logic here
		const nrOfPendingReqs = Object.keys(this.invocations).length;
		if ( nrOfPendingReqs > 0 ) {
			this.warn(`closing socket due to invocation-timeout, number of pending requests: ${nrOfPendingReqs}`);
			// Reject all pending invocations
			for (let invId in this.invocations) {
				let invocation = this.invocations[invId];
				invocation.reject("timeout");
			}
			this.processApplicationSessionTerminated("invocationTimeout");
		}
		else {
			this.debug("invocationTimer expired, no pending requests");
		}
		this.invocationTimer = null;
    }

	async invoke(message: any): Promise<any> {
		let invId = `${this.invokeIdPrefix}${++this.invokeId}`;

		let promise = new Promise((resolve, reject) => this.invocations[invId] = { resolve: resolve, reject: reject });
		// ensure we cleanup the invocation
		promise.finally(() => delete this.invocations[invId]);
		let data: any = Object.values(message)[0];
		data.invokeID = invId;
		if (this.invocationTimeout > 0) {
			this.checkStartInvocationTimer(false);
		}
		this.send(message);
		const nrOfPendingReqs = Object.keys(this.invocations).length;
		await Promise.resolve(); // let WS send happen
		return promise;
	}

	protected startReconnectTimeout() {
		clearTimeout(this.reconnectTimeout);
		if (this.reconnectDelay<=0) {
			this.reconnectDelay = this.reconnectDelayMin;
		} else {
			this.reconnectDelay = Math.min(this.reconnectDelay*2,this.reconnectDelayMax);
		}
		this.info(`start reconnect-timer: ${this.reconnectDelay} ms`);
		this.reconnectTimeout = setTimeout(() => this.onReconnectTimeout(),this.reconnectDelay);
	}

	protected onReconnectTimeout() {
		this.info(`reconnect-timer expired, connected: ${this.connected}, reconnecting: ${this.reconnecting}`);
		if (!this.connected && this.reconnecting) {
			// this will start the next reconnect-timer
			this.reconnect();
		}
	}

	protected onSocketOpen(ws:any) {
		// ensure we're not processing events of old websockets
		if (ws==this.ws) {
			this.info('socket open',this.ws);
			this.connected = true;
			this.reconnectDelay = 0;
			clearTimeout(this.reconnectTimeout);
			this.sendStartApplicationSession();
		}
	}

	protected onSocketError(ws:any,error: any) {
		// ensure we're not processing events of old websockets
		if (ws==this.ws) {
			this.info('socket error:', error);
			this.processApplicationSessionTerminated("error");
		}
	}

	protected onSocketClose(ws:any,error: any) {
		// ensure we're not processing events of old websockets
		if (ws==this.ws) {
			this.info('socket closed:', error);
			this.processApplicationSessionTerminated("error");
		}
	}

	protected onSocketMessage(ws:any,event: any) {
		// ensure we're not processing events of old websockets
		if (ws==this.ws) {
			try {
				let message = JSON.parse(event.data);
				this.info("JSON recv:", event.data);
				this.processMessage(message);
				this.updateKeepalive();
			} catch (error) {
				this.error("cannot process message", error);
			}
		}
	}

	protected getConfigValue(name:string): any {
		return this[name as keyof typeof this] || this.config[name] || this.DEFAULT[name as keyof typeof this.DEFAULT];
	}

	protected updateKeepalive() {
		// calculate the inverval for the next period
		let interval = this.getConfigValue("keepaliveInterval");
		if (this.devices.find(dev => dev.calls.length)) {
			// we're in a call, use other period value
			interval = this.getConfigValue("keepaliveIntervalCall");
		}

		clearTimeout(this.keepaliveResponseTimeout);
		clearTimeout(this.keepaliveSendTimeout);
		this.keepaliveSendTimeout = setTimeout(() => {
			if (this.connected) {
				// send keepalive to verify connection
				this.sendToSocket({Keepalive: {}});

				// setup timer to detect timeout if we don't get a KeepaliveResponse
				const timeout  = this.getConfigValue("keepaliveTimeout");
				clearTimeout(this.keepaliveResponseTimeout);
				this.keepaliveResponseTimeout = setTimeout(() => {
					this.error("keepalive-timeout");
					// #7913: 
					// Better go through the regular termination-path so the
					// "applicationsessionterminated" event is fired and the
					// application can display the lost connection
					this.processApplicationSessionTerminated("keepaliveTimeout");
				},timeout*1000);
			}
		},interval*1000);
		//this.debug(`restart keepalive-timer: ${interval} s`);
	}

	processMessage(message: any) {
		let name = Object.keys(message)[0];
		let content = message[name] || {};

		let device: Device | undefined = this.monitors[content.monitorCrossRefID];
		let call: Call | undefined;
		let invId = content.invokeID;
		let invocation = invId ? this.invocations[invId] : undefined;

		switch (name) {
			// Events
			case 'ServiceInitiatedEvent':
				call = device?.getCall(content.initiatedConnection.callID, true);
				call?.processEvent(name, content);
				break;
			case 'FailedEvent':
				call = device?.getCall(content.failedConnection.callID, true);
				call?.processEvent(name, content);
				break;
			case 'ConnectionClearedEvent':
				call = device?.getCall(content.droppedConnection.callID, true);
				call?.processEvent(name, content);
				break;
			case 'OriginatedEvent':
				call = device?.getCall(content.originatedConnection.callID, true);
				call?.processEvent(name, content);
				break;
			case 'DeliveredEvent':
			case 'DivertedEvent':
				call = device?.getCall(content.connection.callID, true);
				call?.processEvent(name, content);
				break;
			case 'EstablishedEvent':
				call = device?.getCall(content.establishedConnection.callID, true);
				call?.processEvent(name, content);
				break;
			case 'HeldEvent':
				call = device?.getCall(content.heldConnection.callID, true);
				call?.processEvent(name, content);
				break;
			case 'RetrievedEvent':
				call = device?.getCall(content.retrievedConnection.callID, true);
				call?.processEvent(name, content);
				break;
			case 'TransferedEvent': // ecma style...
			case 'TransferredEvent':
				// Don't create call, otherwise we might get duplicated
				// call-instances if an TransferedEvent is emitted twice.
				call = device?.getCall(content.primaryOldCall.callID);
				call?.processEvent(name, content);
				if (content.localConnectionInfo == "null") {
					// secondary old call is gone as well
					call = device.getCall(content.secondaryOldCall?.callID);
					call?.processEvent(name, content);
				}
				break;
			case 'ConferencedEvent':
				call = device?.getCall(content.primaryOldCall.callID, true);
				call?.processEvent(name, content);
				break;
			case 'DtmfDetectedEvent':
				call = device?.getCall(content.overConnection.callID, true);
				call?.processEvent(name, content);
				break;
			case 'StopEvent':
				device?.notify("call", {
					// note that call might be gone already
					name: name,
					content: content,
					call: device.getCall(content.connection.callID)
				});
				break;
			case 'DoNotDisturbEvent':
				device?.processDoNotDisturb(content);
				break;
			case 'ForwardEvent':
				device?.processForward(content);
				break;

			// Proprietary events
			case 'RtcEvent':
				call = device?.getCall(content.connection.callID, true);
				if (this.processRtcEvents) {
					call?.processRtcEvent(content);
				}
				break;
			case 'GenerateDigitsEvent':
				call = device?.getCall(content.connectionToSendDigits.callID, true);
				call?.processGenerateDigits(content.charactersToSend, content.toneDuration);
				break;
			case 'MessageSummaryEvent':
				device?.processMessageSummary(content);
				break;
			case 'ActivityEvent':
				device?.processActivity(content);
				break;
			case 'ActivityUpdatedEvent':
				device?.processUpdatedActivity(content);
				break;
			case 'ActivityDeletedEvent':
				device?.processDeletedActivity(content);
				break;
			case 'TypingEvent':
				device?.processTyping(content);
				break;
			case 'ChatGroupEvent':
				device?.processChatGroup(content);
				break;
			case 'ChatGroupDeletedEvent':
				device?.processChatGroupDeleted(content);
				break;
			case 'ConfigNotificationEvent':
				device?.processConfigNotification(content.data);
				break;
			case 'CallDetailsEvent':
				device?.processCallDetails(content);
				break;

			case 'ConferenceUpdateEvent':
				call = device?.getCall(content.connection?.callID, false);
				if (call) {
					if (this.customConferences) {
						// application wants to handle conference on its own
						this.notify("conferenceupdate", { name, call, content });
					} else if (!content.participants?.length) {
						// no participants means the conference is gone
						call.conference?.processUpdate(content);
						call.conference = undefined;
					} else {
						if (!call.conference) {
							call.conference = new Conference(call,content.participantID);
						} 
						call.conference.processUpdate(content);
					}
				}
				break;
			case 'ConferenceStream':
				call = device?.getCall(content.connection?.callID, false);
				if (this.customConferences) {
					// application wants to handle conference on its own
					this.notify("conferencestream", { name, call, content });
				} else {
					call?.conference?.processStream(content);
				}
				break;

			// events outside of a monitor
			case 'PresenceStateEvent':
				device = this.devices.find(dev => dev.deviceID == content.device);
				device?.processPresenceState(content);
				break;
			case 'ApplicationSessionTerminated':
				this.processApplicationSessionTerminated(content?.sessionTermReason?.definedTermReason);
				break;
			case 'KeepaliveEvent': {
				break;
			}

			case 'CSTAErrorCode':
				invocation?.reject(content);
				break;

			default:
				if (invocation) {
					invocation.resolve(content);
				} else {
					this.info("unknown message:", name);
				}
		}

		const nrOfPendingReqs = Object.keys(this.invocations).length;
		if ( nrOfPendingReqs > 0 && this.invocationTimeout > 0) {
			// we've received something from the server but still have pending requests => restart invocationTimer
			this.checkStartInvocationTimer(true); // restart
		}

		if ( !invocation ) {
			// notify the event
			this.notify("event", {
				deviceID: device?.deviceID,
				monitorCrossRefID: content?.monitorCrossRefID,
				callID: call?.callID,
				name,
				message,
			});
		}
	}

	public reconnect() {
		if (this.url) {
			try {
				let url = this.url;
				if (this.sessionID) {
					url += `?s=${this.sessionID}`;
				}
				this.info('reconnecting to', url);
				const ws = new WebSocket(url);
				this.ws = ws;
				this.ws.onopen = () => this.onSocketOpen(ws);
				this.ws.onerror = (event:any) => this.onSocketError(ws,event);
				this.ws.onclose = (event:any) => this.onSocketClose(ws,event);
				this.ws.onmessage = (event:any) => this.onSocketMessage(ws,event);

				this.startReconnectTimeout();
				return;
			} catch (error) {
				this.error("could not setup socket", error);
			}
		} else {
			this.error("missing url to reconnect");
		}
	}

	protected disconnect(reason: string = "agent disconnect") {
		this.connected = false;
		if (this.ws) {
			try {
				this.ws.onopen = undefined;
				this.ws.onerror = undefined;
				this.ws.onclose = undefined;
				this.ws.onmessage = undefined;
				this.debug("closing websocket");
				this.ws.close(1000 /*NORMAL*/, reason);
				this.debug("websocket closed");
			} catch (error) {
				this.error("websocket cannot close", error);
			}
		}
		this.ws = undefined;
	}

	startApplicationSession(options: anCti.AgentStartApplicationSessionOptions = { token: true }): Promise<anCti.StartApplicationSessionResponse> {
		
		// if there is already a session-promise we reject it immediately
		this.startApplicationSessionPromise?.reject(new Error("aborted by new call to startApplicationSession"));
		this.startApplicationSessionPromise = new PromiseWithResolvers<anCti.StartApplicationSessionResponse>();
		
		this.url = options.url || this.url;
		this.username = options.username || this.username;
		this.password = options.password || this.password;
		this.authentication = options.authentication || this.authentication;
		
		// if these values are set they override the default and the config received from server
		this.invocationTimeout = options.invocationTimeout as number;
		this.keepaliveInterval = options.keepaliveInterval as number;
		this.keepaliveIntervalCall = options.keepaliveIntervalCall as number;
		this.keepaliveTimeout = options.keepaliveTimeout as number;
		this.rtcStatsInterval = options.rtcStatsInterval as number;
		
		this.token = options.token || this.token;
		this.profile = options.profile || this.profile;
		this.accessKey = options.accessKey || this.accessKey;
		this.applicationID = options.applicationID || this.applicationID;
		this.userAgent = options.userAgent || this.userAgent;
		this.sessionDevice = options.sessionDevice || this.sessionDevice;
		this.guest = options.guest;
		this.requestedSessionDuration = options.requestedSessionDuration;
		this.reconnecting = true;
		this.sessionID = options.sessionID || this.sessionID;
		
		if (options.clickToCall) {
			if (typeof options.clickToCall === "string") {
				this.clickToCall = { deviceID: options.clickToCall, audio: true };
			} else {
				this.clickToCall = options.clickToCall;
			}
		}
		if (this.clickToCall || options.reconnect === false) {
			this.reconnecting = false;
		}

		if (this.token === true) {
			// for backward  compatibility, token:true is converted to cookie:"ANAUTH"
			options.cookie = "ANAUTH";
		}

		if (options.cookie) {
			const re = new RegExp(`${options.cookie}=([^;]+)`);
			this.token = Agent.window?.document?.cookie?.match(re)?.[1];
		}

		if (!Agent.window?.TextEncoder || !Agent.window?.crypto?.subtle?.digest) {
			// digest only available on https pages
			this.authentication = "basic";
		}

		if (!this.connected) {
			this.reconnect();
		} else {
			this.sendStartApplicationSession();
		}
		return this.startApplicationSessionPromise.promise;
	}

	async sendStartApplicationSession() {
		try {
			const response = await this.invoke({
				StartApplicationSession: {
					applicationInfo: {
						applicationID: this.applicationID,
						applicationSpecificInfo: {
							username: this.username,
							password: this.authentication == "basic" ? this.password : undefined,
							token: this.token,
							accessKey: this.accessKey,
							userAgent: this.getUserAgent(),
							config: true,
							clickToCall: this.clickToCall,
							guest: this.guest,
							profile: this.profile,
							sessionDevice: this.sessionDevice,
						}
					}
				}
			});
			if (response.extensions?.nonce && this.username && this.password && this.authentication == "digest") {
				const msgUint8 = new TextEncoder().encode(this.username + ":" + this.password + ":" + response.extensions.nonce); // encode as (utf-8) Uint8Array
				const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8);
				const hashArray = Array.from(new Uint8Array(hashBuffer));
				const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
				const response2 = await this.invoke({
					StartApplicationSession: {
						applicationInfo: {
							applicationID: this.applicationID,
							applicationSpecificInfo: {
								username: this.username,
								response: hashHex,
								userAgent: this.getUserAgent(),
								config: true,
								profile: this.profile,
								sessionDevice: this.sessionDevice,
							}
						},
						requestedSessionDuration: this.requestedSessionDuration
					}
				});
				this.processStartApplicationSessionResponse(response2);
			} else {
				this.processStartApplicationSessionResponse(response);
			}
		} catch(error) {
			this.error("could not start application,session",error)
			this.startApplicationSessionPromise?.reject(error);
		}
	}

	private processStartApplicationSessionResponse(response: any) {
		if (response.errorCode) {
			this.processApplicationSessionTerminated(response.errorCode);
		} else {
			this.sessionID = response.sessionID;
			this.sessionDuration = response.actualSessionDuration;
			Object.assign(this.config, response.config);

			this.config.reconnected = this.reconnecting;

			// apply configured values
			this.updateKeepalive();
			this.updateRtcStatsInterval();

			// automatically initialise devices with received config
			this.config.deviceList?.forEach((cfg: any) => {
				const device: any = this.getDevice(cfg.deviceID);
				device.autoAnswer = cfg.autoAnswer;
				device.type = cfg.type;
				device.name = cfg.name;
				device.publicNumber = cfg.publicNumber;
				device.number = cfg.number;
				device.terminal = cfg.terminal;
				device.rtc = cfg.rtc;
			});

			if (response.config?.token && Agent.window?.document) {
				// for now the repo uses cookies
				try {
					// evaluate expiry-time of token to use it for cookie as well
					const base64 = response.config.token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
					const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(c => {
						return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
					}).join(''));
					const json: any = JSON.parse(jsonPayload);
					const expires = new Date(json.exp*1000);
					Agent.window.document.cookie = `ANAUTH=${response.config.token}; expires=${expires.toUTCString()}; path=/`;
				} catch(error) {
					this.error("could not set cookie",error);
				}
			}

			this.notify("applicationsessionstarted", this.config);
			this.startApplicationSessionPromise?.resolve(response);
			
			while (this.queue.length) {
				this.send(this.queue.shift());
			}

			if (response.clickToCall) {
				// the session automatically monitors the clickToCall device
				const device = this.getDevice(response.clickToCall.deviceID);
				const monitorCrossRefID = response.monitorCrossRefID;
				this.monitors[monitorCrossRefID] = device;
				device.monitorCrossRefID = monitorCrossRefID;
			} else if (response.config?.guest) {
				const device = this.getDevice(response.config.guest.deviceID);
				const monitorCrossRefID = response.monitorCrossRefID;
				this.monitors[monitorCrossRefID] = device;
				device.monitorCrossRefID = monitorCrossRefID;
			}

			// sync ongoing calls
			// (for simplicity use processRtcEvents to detect if we are in a "split" agent
			// where calls are not handled by this instance, but rather in a renderer)
			if (this.processRtcEvents) {
				this.devices.forEach(dev => {
					dev.getCalls().forEach(async call => call.restartCall());
				});
			}
		}
	}

	private processApplicationSessionTerminated(reason: string) {
		if (reason == 'shutdown' || 
				reason == 'normal' || 
				reason == 'invalidConferencePin' || 
				reason == 'invalidApplicationInfo') {
			this.reconnecting = false;
		} else if (reason == 'error' ||
				reason == 'invocationTimeout' ||
				reason == 'keepaliveTimeout' ||
				reason == 'licenseUpdate') {
			this.reconnecting = true;
		}

		// ensure we're not using clickToCall the next time we connect
		this.clickToCall = undefined;
		
		const connectedBefore = this.connected;
		this.ws && this.disconnect(reason);

		if (connectedBefore) {
			this.eventEmitter.notify("applicationsessionterminated", {
				reason: reason,
				reconnecting: this.reconnecting,
			});
		}

		if (this.reconnecting) {
			this.startReconnectTimeout();
		} else {
			this.sessionID = undefined;
			this.config = {};
			this.devices.forEach(device => device.calls.forEach(call => call.shutdown()));

			this.startApplicationSessionPromise?.reject(new Error(reason));
		}
	}

	stopApplicationSession(options?: anCti.StopApplicationSessionOptions) {
		this.send({
			StopApplicationSession: {
				sessionID: this.sessionID,
				sessionEndReason: {
					appEndReason: options?.reason || "normal",
				}
			}
		})
		this.processApplicationSessionTerminated("normal");
	}

	// used for example after power-resume to reconnect as quick as possible
	restartApplicationSession(delayMillis?: number) {
		this.disconnect();
		this.reconnecting = true;
		this.reconnectDelay = 0;
		this.eventEmitter.notify("applicationsessionterminated", {
			reason: "restart",
			reconnecting: this.reconnecting,
		});
		this.startReconnectTimeout();
	}

	resetApplicationSessionTimer(requestedSessionDuration: number) {
		this.invoke({
			ResetApplicationSessionTimer: {
				requestedSessionDuration: requestedSessionDuration
			}
		}).then((response: any) => {
			this.sessionDuration = response.actualSessionDuration;
		});
	}

	async readDirectories(options: anCti.ReadDirectoriesOptions): Promise<anCti.ReadDirectoriesResponse> {
		let response: any = await this.invoke({
			ReadDirectories: {
				text: options.text,
				limit: options.limit,
				scope: options.scope,
				match: options.match,
				avatars: options.avatars,
				deviceIds: options.deviceIds,
				types: options.types,
				fuzzy: (options as any).fuzzy, // experimental
			}
		});
		return response.entries;
	}

	// Utility functions
	parseDeviceID(deviceID: any, target: any = {}) {
		if (deviceID) {
			if (deviceID.hasOwnProperty("deviceIdentifier")) {
				deviceID = deviceID.deviceIdentifier;
			}
			if (deviceID) {
				let match = deviceID.match(/sip:([^@]*)/);
				if (match) {
					target.number = match[1]
					target.name = ""
				} else {
					match = deviceID.match(/N<([^>]*)>(.*)/);
					if (match) {
						target.number = match[1]
						target.name = match[2]
					}
				}
			}
		}
		return target
	}

	getMember(obj: any, ...path: string[]): any {
		for (const name of path) {
			obj[name] = obj[name] || {};
			obj = obj[name];
		}
		return obj;
	}

	private removeUndefinedMembers(obj: any) {
		try {
			Object.keys(obj).forEach((key) => {
				let val = obj[key];
				if (val === undefined || val == null) {
					delete obj[key];
				} else if (typeof val === 'object') {
					if (Object.keys(val).length == 0) {
						// RTCSessionDescription and others have no keys, just symbols. Just delete if there are no symbols.
						if (Object.getOwnPropertySymbols(Object.getPrototypeOf(val)).length == 0) {
							delete obj[key];
						}
					} else {
						this.removeUndefinedMembers(val);
					}
				}
			});
		} catch (error) {
			this.error("cannot remove undefined members", error);
		}
	}

	getUserAgent() {
		if (!this.userAgent) {
			this.userAgent = this.detectBrowser();
		}
		return this.userAgent;
	}

	private detectBrowser(): string {
		// https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent
		if (Agent.window?.navigator?.userAgent) {
			let ua = navigator.userAgent;
			let chrome = /Chrome\/([0-9.]+)/.exec(ua)?.[1];
			let firefox = /Firefox\/([0-9.]+)/.exec(ua)?.[1];
			let seamonkey = /Seamonkey\/([0-9.]+)/.exec(ua)?.[1];
			let chromium = /Chromium\/([0-9.]+)/.exec(ua)?.[1];
			let safari = /Safari\/([0-9.]+)/.exec(ua)?.[1];
			let opera = /OPR\/([0-9.]+)/.exec(ua)?.[1];

			if (firefox && !seamonkey) return `firefox/${firefox}`;
			if (seamonkey) return `seamonkey/${seamonkey}`;
			if (chrome && !chromium) return `chrome/${chrome}`;
			if (chromium) return `chromium/${chromium}`;
			if (safari && !chrome && !chromium) return `safari/${safari}`;
			if (opera) return `opera/${opera}`;
		}
		return "unknown";
	}

	async requestSystemStatus(): Promise<anCti.RequestSystemStatusResponse> {
		return this.invoke({
			RequestSystemStatus: {
			}
		});
	}

	async logRtcStats(options:any,pc?:RTCPeerConnection) {
		if (!pc) return;
		const stats = await pc.getStats()
		stats?.forEach(report => {
			switch (report.type) {
				case "outbound-rtp":
				case "inbound-rtp":
				case "remote-inbound-rtp":
				case "remote-outbound-rtp":
					Object.assign(report,options);
					this.debug(`RTCStatsReport ${JSON.stringify(report)}`);
			}
		});
	}

	logAllRtcStats() {
		this.getDevices().forEach(device => {
			device.getCalls().forEach(call => {
				this.logRtcStats({
					connId: `call:${call.callID}`,
					connName: call.name || call.number,
				},call.pc)
				const conf = call.conference;
				conf?.getParticipants()?.forEach(participant => {
					this.logRtcStats({
						connId: `participant:${participant.id}`,
						connName: participant.displayName,
					},(participant as any).remoteStream?.pc);
				});
			});
		});
	}

	setRtcStatsInterval(millis:number) {
		this.rtcStatsInterval = millis;	
		this.updateRtcStatsInterval();
	}

	protected updateRtcStatsInterval() {
		clearInterval(this.rtcStatsTimer);
		const interval = this.getConfigValue("rtcStatsInterval");
		if (interval) {
			this.rtcStatsTimer = setInterval(() => this.logAllRtcStats(),interval*1000);
		}
	}

	setupLogging(options: anCti.LoggingOptions = {}) {
		const {
			maxLineLength = 2048,
			maxArrayLength = 4,
			maxDepth = 3,
			logPrivateMembers = false,
			tee = undefined,
		} = options;

		const stringifyObject = (obj:any,depth:number,seen:WeakSet<any>) => {

			if (obj==null) return "null";

			// allow big objects to define how they appear in log
			if (typeof obj.stringify=="function") return obj.stringify();

			switch (typeof(obj)) {
				case "object":
					if (seen.has(obj)) return "<circular>";
					seen.add(obj);

					let str = "";
					let sep = "";
					if (Array.isArray(obj)) {
						str += "[";
						if (depth<maxDepth) {
							let len = Math.min(obj.length,maxArrayLength);
							for (let i=0;i<len;i++) {
								str += `${sep}${stringifyObject(obj[i],depth+1,seen)}`
								if (str.length>maxLineLength) return `${str}...`;
								sep = ", ";
							}
							if (obj.length>maxArrayLength) {
								str += `, ...${obj.length-maxArrayLength}`;
							}
						} else {
							str += "...";
						}
						str += "]";
					} else {
						str += "{";
						if (depth<maxDepth) {
							for (const prop in obj) {
								// do not show functions as members
								if (typeof(obj[prop])=="function") continue;
								if (!logPrivateMembers && prop.startsWith("_")) continue;
								str += `${sep}${prop}: ${stringifyObject(obj[prop],depth+1,seen)}`
								if (str.length>maxLineLength) return `${str}...`;
								sep = ", ";
							}
						} else {
							str += "...";
						}
						str += "}";
					}
					return str;
				case "string": return depth<=0?obj:`'${obj}'`;
				case "function": return `${obj.name}()`;
				case "undefined": return "undefined";
				default: return obj.toString()
			}
		}

		const format = (...args:any) => {
			const seen = new WeakSet();
			let str = "";
			let sep = "";
			args.forEach((val:any) => {
				str += `${sep}${stringifyObject(val,0,seen)}`
				sep = " ";
			});
			return str;
		}

		// reassing logging methods
		this.error = (...args:any) => {
			const line = format(...args);
			console.error.apply(console,[line]);
			tee?.("error",line);
		}

		this.warn = (...args:any) => {
			const line = format(...args);
			console.warn.apply(console,[line]);
			tee?.("warn",line);
		}

		this.info = (...args:any) => {
			const line = format(...args);
			console.info.apply(console,[line]);
			tee?.("info",line);
		}

		this.debug = (...args:any) => {
			const line = format(...args);
			console.debug.apply(console,[line]);
			tee?.("debug",line);
		}
	}

}


export function newAgent() {
	return new Agent();
}
